import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useVideoSystem, Video } from '../../hooks/useVideoSystem';

interface VideoPlayerModalProps {
  video: Video;
  onClose: () => void;
}

function VideoPlayerModal({ video, onClose }: VideoPlayerModalProps) {
  const getEmbedUrl = () => {
    switch (video.type) {
      case 'youtube': {
        // Extraire l'ID de la vidéo de différents formats d'URL YouTube
        let videoId = video.videoId;
        if (!videoId && video.url) {
          const url = new URL(video.url);
          if (url.hostname.includes('youtube.com')) {
            const paramValue = url.searchParams.get('v');
            videoId = paramValue || undefined;
          } else if (url.hostname === 'youtu.be') {
            videoId = url.pathname.slice(1);
          }
        }
        return `https://www.youtube-nocookie.com/embed/${videoId}`;
      }
      case 'vimeo':
        return `https://player.vimeo.com/video/${video.videoId}`;
      case 'dailymotion':
        return `https://www.dailymotion.com/embed/video/${video.videoId}`;
      default:
        return video.url;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="relative w-full max-w-4xl bg-black rounded-xl overflow-hidden">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>
        
        <div className="aspect-video">
          <iframe
            src={getEmbedUrl()}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={video.title}
          />
        </div>
      </div>
    </div>
  );
}

export function VideosApp() {
  const { chatbotPublicId } = useChatbot();
  const { settings, videos, loading, initialize } = useVideoSystem();
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const { colors } = useDesignColors();

  useEffect(() => {
    if (chatbotPublicId) {
      initialize(chatbotPublicId);
    }
  }, [chatbotPublicId, initialize]);

  if (loading.initialize && videos.length === 0) {
    return (
      <div className="flex justify-center items-center h-[500px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="text-center text-gray-500 p-4">
        No videos in the gallery
      </div>
    );
  }

  const renderVideoThumbnail = (video: Video) => {
    // Fonction pour obtenir l'URL de la miniature YouTube
    const getYouTubeThumbnail = (videoId: string) => {
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    };

    // Obtenir l'URL de la miniature en fonction du type de vidéo
    const getThumbnailUrl = () => {
      if (video.thumbnailUrl) {
        return video.thumbnailUrl;
      }
      if (video.type === 'youtube' && video.videoId) {
        return getYouTubeThumbnail(video.videoId);
      }
      return null;
    };

    const thumbnailUrl = getThumbnailUrl();

    return (
      <div className="relative aspect-video rounded-lg overflow-hidden group">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={video.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-black/20 flex items-center justify-center">
            <Play className="w-12 h-12 text-white/50" />
          </div>
        )}
        
        <div 
          className="absolute inset-0 flex flex-col justify-between p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-100 transition-opacity"
        >
          <div className="flex-1" />
          <div>
            <h3 className="text-white font-medium text-lg mb-1">{video.title}</h3>
            {video.description && (
              <p className="text-white/80 text-sm line-clamp-2">{video.description}</p>
            )}
          </div>
        </div>
        
        <div 
          className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background: `linear-gradient(135deg, ${colors.gradientStart}80, ${colors.gradientEnd}80)`,
          }}
        >
          <button
            onClick={() => setSelectedVideo(video)}
            className="p-4 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors transform scale-90 group-hover:scale-100 duration-300"
          >
            <Play className="w-8 h-8" />
          </button>
        </div>
      </div>
    );
  };

  if (settings.displayMode === 'carousel') {
    return (
      <div className="relative w-full h-[500px]">
        {renderVideoThumbnail(videos[currentIndex])}
        
        {videos.length > 1 && (
          <>
            <button
              onClick={() => setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1))}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={() => setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1))}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </>
        )}
      </div>
    );
  }

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${settings.gridColumns}, 1fr)`,
    gap: `${settings.spacing * 0.25}rem`,
  };

  return (
    <>
      <div className="w-full h-[500px] overflow-y-auto p-4">
        <div style={gridStyle}>
          {videos.map((video) => (
            <div key={video.id}>
              {renderVideoThumbnail(video)}
            </div>
          ))}
        </div>
      </div>

      {selectedVideo && (
        <VideoPlayerModal
          video={selectedVideo}
          onClose={() => setSelectedVideo(null)}
        />
      )}
    </>
  );
}